import { get_token } from "@/router";
import {
    dedupe_marketplace_listings,
    format_price,
    view_profile_product as view_service_details_helper
} from "@/views/Marketplace/marketplace_helpers";
import {
    add_attribute_value,
    group_attributes,
    remove_attribute
} from "@/views/BusinessProfileView/business_profile_helper";
import { auto_complete_map } from "@/components/Delivery/delivery_helpers";

export function create_item_form() {
    return ({
        name: "",
        price: null,
        description: "",
        service_type: null,
        service_type_id: null,
        service_location: "",
        attributes: {}
    });
}

export function create_update_item_form() {
    return {
        product_id: "",
        name: "",
        price: "",
        description: "",
        subcategory_id: "",
        attributes: {},
        service_type_id: "",
        service_type: "",
        location: "",
    };
}

function find_drop_in_service_type(service_types = []) {
    return service_types.find((type) => String(type.name || "").toLowerCase() === "drop_in");
}

function get_next_base_profile_id(profile, current_base_profile_id) {
    return current_base_profile_id || profile?.id || null;
}

function get_next_shop_profile(profile, current_shop_profile) {
    return profile || current_shop_profile;
}

function should_add_attribute_from_key(event, type) {
    return Boolean(type && ["Enter", ","].includes(event?.key));
}

function has_required_service_type(context) {
    if (context.item_form.service_type_id) return false;
    context.errors.service_type = "Please select a service type";
    return true;
}

function has_required_dropin_location(context) {
    if (!context.is_dropin_selected || context.item_form.service_location?.trim()) return false;
    context.errors.service_location = "Location is required for drop-in services";
    return true;
}

function has_required_service_name(context) {
    if (context.item_form.name?.trim()) return false;
    context.errors.name = "Service name is required";
    return true;
}

function has_required_service_price(context) {
    if (context.item_form.price !== "" && context.item_form.price !== null) return false;
    context.errors.price = "Service price is required";
    return true;
}

function has_positive_service_price(context) {
    if (Number(context.item_form.price) > 0) return false;
    context.errors.price = "Price must be greater than 0";
    return true;
}

function get_date_suffix(day) {
    if (day > 3 && day < 21) return "th";
    const suffixes = { 1: "st", 2: "nd", 3: "rd" };
    return suffixes[day % 10] || "th";
}

export const services_view_methods = {
    getToken() {
        return get_token();
    },
    sync_catalogue_items(items = []) {
        const deduped_items = dedupe_marketplace_listings(items);
        this.profile_products = deduped_items;
        this.catalogue_services = deduped_items;
    },
    set_active_home_profile(profile) {
        this.active_home_profile = profile || null;
        this.base_profile_id = get_next_base_profile_id(profile, this.base_profile_id);
        this.shop_profile = get_next_shop_profile(profile, this.shop_profile);
    },
    open_business_profile_from_actions() {
        this.$refs.homeNavBarRef?.open_company_profile_modal?.();
    },
    validate_service_form() {
        if (this.modalType !== "service") return false;
        return [
            has_required_service_type(this),
            has_required_dropin_location(this),
        ].some(Boolean);
    },
    go_back() {
        this.$router.back();
    },
    view_service_details(service) {
        view_service_details_helper(this.$router, service);
    },
    initialize_add_item_location_autocomplete() {
        const input_element = this.$refs.addItemLocationInput;

        auto_complete_map(input_element, (address) => {
            this.item_form.service_location = address;
            this.errors.service_location = null;
        }).catch((error) => {
            console.error('Failed to load Google address suggestions:', error);
        });
    },
    set_modal_type(type) {
        this.modalType = this.is_services_page ? "service" : type;
        this.selected_category = "";
        this.selected_subcategory = "";
        this.item_form.attributes = {};
        this.item_form.service_location = "";
        this.item_form.service_type_id = null;
        this.set_default_service_type();
        this.fetch_categories();
    },
    set_default_service_type() {
        if (this.modalType !== "service" || this.item_form.service_type_id) return;
        this.item_form.service_type_id = find_drop_in_service_type(this.service_types)?.id || null;
    },
    apply_default_service_type(service_types = this.service_types) {
        if (this.modalType !== "service" || this.item_form.service_type_id) return;
        this.item_form.service_type_id = find_drop_in_service_type(service_types)?.id || null;
    },
    validate_item_form() {
        this.errors = {};
        return [
            has_required_service_name(this),
            has_required_service_price(this) || has_positive_service_price(this),
            this.validate_service_form(),
        ].some(Boolean);
    },
    reset_product_form() {
        this.errors = {};
        this.item_form = create_item_form();
        this.update_item_form = create_update_item_form();
        this.modalType = this.add_item_modal_type;
        this.isEditing = false;
        this.selected_category = "";
        this.selected_subcategory = "";
        this.subcategories = [];
        this.upload_files = [];
        this.previews = [];
        this.isLoading = false;
        this.new_attribute_value = {};
        this.active_modal_image_index = 0;
    },
    cancel_edit() {
        this.isEditing = false;
        this.reset_product_form();
    },
    close_add_item_modal() {
        this.reset_product_form();
        $("#addItemModal").modal("hide");
    },
    close_manage_modal() {
        this.reset_product_form();
        $("#manageProductModal").modal("hide");
    },
    handleImagesUpload(event) {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;
        const supported_files = files.filter((file) => this.is_supported_media_file(file));
        const has_unsupported_files = supported_files.length !== files.length;

        if (has_unsupported_files) {
            this.errors.product_images = "Only image and video files can be uploaded.";
        }

        if (!supported_files.length) {
            event.target.value = "";
            return;
        }

        if (!has_unsupported_files && supported_files.some((file) => file.type.startsWith("image/"))) {
            this.errors.product_images = null;
        }
        supported_files.forEach((file) => {
            this.add_preview_file(file);
        });
        event.target.value = "";
    },
    is_supported_media_file(file) {
        return file?.type?.startsWith("image/") || file?.type?.startsWith("video/");
    },
    add_preview_file(file) {
        this.upload_files.push(file);
        if (file.type.startsWith("video/")) {
            this.previews.push({ type: "video", src: URL.createObjectURL(file), name: file.name, file });
            return;
        }
        const reader = new FileReader();
        reader.onload = (load_event) => {
            this.previews.push({ type: "image", src: load_event.target.result, name: file.name, file });
        };
        reader.readAsDataURL(file);
    },
    handle_drop(event) {
        this.isDragging = false;
        const files = event.dataTransfer.files;
        if (files.length > 0) this.handleImagesUpload({ target: { files } });
    },
    remove_file(index) {
        const file = Reflect.get(this.previews, index);
        if (file.id) {
            file._delete = true;
            return;
        }
        const fileIndex = this.upload_files.indexOf(file.file);
        if (fileIndex > -1) this.upload_files.splice(fileIndex, 1);
        this.previews.splice(index, 1);
    },
    prev_modal_image() {
        const total = this.modal_product?.images?.length || 0;
        if (!total) return;
        this.active_modal_image_index = (this.active_modal_image_index - 1 + total) % total;
    },
    next_modal_image() {
        const total = this.modal_product?.images?.length || 0;
        if (!total) return;
        this.active_modal_image_index = (this.active_modal_image_index + 1) % total;
    },
    add_attribute_value(type) {
        const target_form = this.isEditing ? this.update_item_form : this.item_form;
        add_attribute_value(target_form, this.new_attribute_value, type);
    },
    handle_attribute_input_keydown(event, type) {
        if (!should_add_attribute_from_key(event, type)) return;
        event.preventDefault();
        this.add_attribute_value(type);
    },
    group_attributes(attributes) {
        return group_attributes(attributes);
    },
    remove_attribute(item_form, type_name, index) {
        return remove_attribute(item_form, type_name, index);
    },
    display_price(service) {
        return service?.final_price ?? service?.price ?? 0;
    },
    get_request_service_name(request) {
        return request?.service?.name || request?.product?.name || "";
    },
    format_price(value) {
        return format_price(value);
    },
    format_date(datestr) {
        const date = new Date(datestr);
        const day = date.getDate();
        const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
        const month = date.toLocaleDateString("en-US", { month: "long" });
        return `${weekday}, ${month} ${day}${get_date_suffix(day)}, ${date.getFullYear()}`;
    },
};



